import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { authClient } from '@solum/auth'
import {
  advanceRecovery, custodianRecoveryCases, recoveryCases, recoveryContexts, requestRecovery,
  verifyRecoveryEvidence, type CustodianRecoveryCase, type RecoveryCase, type RecoveryContext,
  type RecoveryEvidenceInput,
} from '@solum/sdk'
import {
  Button, FactList, Field, Input, PageSection, RecoverySurface, Select,
  type RecoverySurfaceCase, type RecoverySurfaceContext,
} from '@solum/ui'
import { useSolum } from '../data/client'

const key = ['access-recovery', 'console'] as const
const blank: RecoveryEvidenceInput = {
  primaryClass: 'recent_primary_session', primaryDigest: '', primaryReference: '',
  independentClass: 'known_contact_callback', independentDigest: '', independentReference: '',
}
const presentContext = (row: RecoveryContext): RecoverySurfaceContext => ({
  principalClass: row.principal_class, accountId: row.account_id, portalId: row.portal_id,
  accountName: row.account_name, portalName: row.portal_name,
})
const presentCase = (row: RecoveryCase): RecoverySurfaceCase => ({
  id: row.id, state: row.state, principalClass: row.principal_class,
  accountId: row.account_id, portalId: row.portal_id, createdAt: row.created_at,
  expiresAt: row.expires_at, lastFailureCode: row.last_failure_code,
})
const words = (error: unknown) => error instanceof Error ? error.message : 'Recovery could not continue.'

/** Console host adapter. The shared subject surface and the custodian evidence
 * desk consume only governed doors; neither reads a closed recovery table. */
export function ConsoleRecoveryPanel({ onRecovered }: { onRecovered: () => void }) {
  const { client } = useSolum()
  const raw = client.raw()
  const qc = useQueryClient()
  const contexts = useQuery({ queryKey: [...key, 'contexts'], queryFn: () => recoveryContexts(raw) })
  const cases = useQuery({ queryKey: [...key, 'cases'], queryFn: () => recoveryCases(raw) })
  const custody = useQuery({
    queryKey: [...key, 'custody'],
    queryFn: async () => {
      try { return await custodianRecoveryCases(raw) }
      catch (error) { if (String(error).includes('403')) return []; throw error }
    },
  })
  const refresh = () => qc.invalidateQueries({ queryKey: [...key] })
  const request = useMutation({ mutationFn: (context: RecoveryContext) => requestRecovery(raw, context), onSuccess: refresh })
  const advance = useMutation({
    mutationFn: async (row: RecoveryCase) => advanceRecovery(raw, row, await authClient.getAccessToken()),
    onSuccess: async (state) => { await refresh(); if (state === 'completed') onRecovered() },
  })
  const visible = (contexts.data ?? []).filter((row) => row.principal_class === 'console')
  const subjectError = request.error ?? advance.error ?? contexts.error ?? cases.error

  return <div data-module="access-recovery">
    <RecoverySurface contexts={visible.map(presentContext)} cases={(cases.data ?? []).map(presentCase)}
      busy={request.isPending || advance.isPending} error={subjectError ? words(subjectError) : null}
      onRequest={(row) => {
        const context = visible.find((x) => x.account_id === row.accountId && x.portal_id === row.portalId)
        if (context) request.mutate(context)
      }}
      onAdvance={(row) => {
        const recoveryCase = (cases.data ?? []).find((x) => x.id === row.id)
        if (recoveryCase) advance.mutate(recoveryCase)
      }} />
    {(custody.data?.length ?? 0) > 0 ? <CustodianEvidenceDesk rows={custody.data ?? []}
      onVerified={async () => { await refresh() }} raw={raw} /> : null}
    {custody.error ? <p role="alert">{words(custody.error)}</p> : null}
  </div>
}

function CustodianEvidenceDesk({ rows, raw, onVerified }: {
  rows: CustodianRecoveryCase[]
  raw: ReturnType<ReturnType<typeof useSolum>['client']['raw']>
  onVerified: () => Promise<void>
}) {
  const [caseId, setCaseId] = useState(rows[0]?.id ?? '')
  const [evidence, setEvidence] = useState<RecoveryEvidenceInput>(blank)
  const chosen = rows.find((row) => row.id === caseId) ?? rows[0]
  const verify = useMutation({
    mutationFn: () => {
      if (!chosen) throw new Error('Choose a recovery case.')
      return verifyRecoveryEvidence(raw, chosen, evidence)
    },
    onSuccess: async () => { setEvidence(blank); await onVerified() },
  })
  const digestOk = /^[0-9a-f]{64}$/i.test(evidence.primaryDigest)
    && /^[0-9a-f]{64}$/i.test(evidence.independentDigest)
  const referencesOk = evidence.primaryReference.includes(':') && evidence.independentReference.includes(':')
  return <PageSection title="Verify recovery evidence" headingLevel={3} testId="recovery-custodian"
    summary="Only safe references and SHA-256 digests enter Marble. Do not paste recordings, documents, passwords or factor values."
    primaryAction={<Button variant="primary" disabled={verify.isPending || !chosen || !digestOk || !referencesOk}
      data-testid="recovery-verify" onClick={() => verify.mutate()}>Record verified evidence</Button>}>
    <Select value={chosen?.id ?? ''} onValueChange={setCaseId}
      aria-label="Recovery case" testId="recovery-custodian-case"
      options={rows.map((row) => ({ value: row.id, label: `${row.account_name} · ${row.principal_class} · ${row.id.slice(0, 8)}` }))} />
    {chosen ? <FactList layout="rows" facts={[
      { key: 'account', label: 'Account', value: chosen.account_name },
      { key: 'surface', label: 'Sign-in surface', value: chosen.portal_name ?? 'Console' },
      { key: 'subject', label: 'Subject login', value: chosen.subject_login_id },
      { key: 'expires', label: 'Case expires', value: chosen.expires_at },
    ]} /> : null}
    <Field label="Primary-authentication evidence">
      <Select value={evidence.primaryClass}
        onValueChange={(primaryClass) => setEvidence((old) => ({ ...old, primaryClass: primaryClass as RecoveryEvidenceInput['primaryClass'] }))}
        options={[{ value: 'recent_primary_session', label: 'Recent primary session' },
          { value: 'password_reauthentication', label: 'Password reauthentication' }]} />
    </Field>
    <Field label="Primary evidence reference" hint="An external system reference, for example idp:case-123.">
      <Input value={evidence.primaryReference} onChange={(event) => setEvidence((old) => ({ ...old, primaryReference: event.target.value }))} />
    </Field>
    <Field label="Primary evidence SHA-256 digest">
      <Input value={evidence.primaryDigest} onChange={(event) => setEvidence((old) => ({ ...old, primaryDigest: event.target.value.trim() }))} />
    </Field>
    <Field label="Independent human evidence">
      <Select value={evidence.independentClass}
        onValueChange={(independentClass) => setEvidence((old) => ({ ...old, independentClass: independentClass as RecoveryEvidenceInput['independentClass'] }))}
        options={[{ value: 'known_contact_callback', label: 'Known-contact callback' },
          { value: 'human_video', label: 'Human video check' }, { value: 'in_person', label: 'In person' }]} />
    </Field>
    <Field label="Independent evidence reference" hint="An external system reference, never the evidence itself.">
      <Input value={evidence.independentReference} onChange={(event) => setEvidence((old) => ({ ...old, independentReference: event.target.value }))} />
    </Field>
    <Field label="Independent evidence SHA-256 digest">
      <Input value={evidence.independentDigest} onChange={(event) => setEvidence((old) => ({ ...old, independentDigest: event.target.value.trim() }))} />
    </Field>
    {verify.error ? <p role="alert">{words(verify.error)}</p> : null}
  </PageSection>
}
